import cx from 'classnames'
import type { FC } from 'react'

import { Checkbox } from '../Checkbox'
import { CollapsableBlock } from '../CollapsableBlock'
import { EllipsisLabelWithTitle } from '../EllipsisLabelWithTitle'
import css from './CheckboxTree.module.scss'
import type { CheckboxTreeItem, CheckboxTreeState } from './types'

type Props = {
  data: CheckboxTreeState
  nodeId: string
  toggleStatus: (id: string, status: boolean) => void
  isNested?: boolean
}

interface ChildProps {
  node: CheckboxTreeItem
}

const Title: FC<ChildProps> = ({ node }) => (
  <EllipsisLabelWithTitle className={cx({ [css.crossedOut]: !node.checked })}>
    {node.prefix || '/'}
  </EllipsisLabelWithTitle>
)

interface ControlProps extends ChildProps {
  onChange: (status: boolean) => void
}

const Control: FC<ControlProps> = ({ node, onChange }) => (
  <Checkbox
    labelIsOnLeftSide
    label={node.label}
    onChange={onChange}
    checked={node.checked}
    indeterminate={node.indeterminate}
    size="large"
  />
)

const CheckboxSubTree: FC<Props> = ({
  data,
  nodeId,
  toggleStatus,
  isNested,
}) => {
  const node = data.get(nodeId)
  if (!node) {
    return null
  }

  const onChange = (status: boolean) => toggleStatus(node.id, status)

  return node.children?.length ? (
    <CollapsableBlock
      title={<Title node={node} />}
      control={<Control node={node} onChange={onChange} />}
      upperUnderline={isNested ? 'partial' : undefined}
      className={css.collapsableBlock}
      withoutContentPadding
      isOpenedOnStart={!isNested}
    >
      <div className={css.branchContent}>
        {node.children.map((childId) => (
          <CheckboxSubTree
            data={data}
            key={childId}
            nodeId={childId}
            toggleStatus={toggleStatus}
            isNested
          />
        ))}
      </div>
    </CollapsableBlock>
  ) : (
    <div className={css.leaf}>
      <div className={css.leafTitle}>
        <Title node={node} />
      </div>
      <Control node={node} onChange={onChange} />
    </div>
  )
}

export default CheckboxSubTree
